import {copyVectorArray, findChildrenByName, getPointsFromBoundingBox2D, setBox3FromObjectArray} from "./threeUtils";
import {getOuterWalls} from "./configuratorUtils";
import {Box3, Vector3, BoxGeometry, Mesh,MeshBasicMaterial} from "three";
import {sortBySubKey, toFixedFloat, toFixedVector} from "./utils";
import {getIntersectingWalls, getWallOrientation} from "../models/walls/wall";
import {signedAngle} from "./mathUtils";
import {buildingGroup} from "../main";
import {supportBeamMeshName} from "../constants/nameConstants";
import {createShapeFromPoints, pathPointsClockwise, shapeContainsPoints} from "./shapeUtils";
import {Roof} from "../models/roofs/roof";
import {shellOutline} from "./rectOutline";

//Automatische Bestimmung Dach/Bodenpunkte aus Außenwänden
//TODO: Dupliziert aus measurements.js > Zusammenfassen
let walls = {
    left: [],
    right: [],
    front: [],
    back: []
}; //Nach position aufgeteilte Wände

export const wallSides = {
    left: 1,
    back: 2,
    right: 3,
    front: 4
};

export function getOuterWallPoints(setTopPosition = false, includeSupportBeams = true/*always true for roof*/, silent = false) {
    let outerWalls = getOuterWalls();
    let supportBeams = findChildrenByName(buildingGroup, supportBeamMeshName, true, true);
    // A closed shell is four outer walls, or four sides made from walls and beams together.
    if (outerWalls.length < 4 && outerWalls.length + supportBeams.length < 4) {
        return [];
    }

    let yPosition = 0;
    if (setTopPosition) {
        const ySource = outerWalls.length > 0 ? outerWalls : supportBeams;
        if (ySource.length > 0) {
            yPosition = setBox3FromObjectArray(ySource).max.y;
        }
    }

    let points = [];
    if (outerWalls.length >= 4) {
        points = pointsFromShell(outerWalls, yPosition, silent);

        // Beams that sit outside a finished wall loop extend or replace that side.
        if (supportBeams.length > 0 && points.length > 0 && includeSupportBeams) {
            let filteredSupportBeams = getSupportBeamsOutsideBuilding(points, supportBeams);
            if (filteredSupportBeams.length > 0) {
                let extended = pointsFromShell(outerWalls.concat(filteredSupportBeams), yPosition, silent);
                if (extended.length < 4) {
                    // A terrace narrower than the house meets the wall mid-span, which the loop walk cannot follow.
                    extended = pointsFromOutline(outerWalls.concat(filteredSupportBeams), yPosition);
                }
                if (extended.length > 3) {
                    points = extended;
                }
            }
        }
    }

    // Beams can be the shell by themselves, or finish a loop that does not yet have four walls.
    if (points.length < 4 && supportBeams.length > 0) {
        let mixed = pointsFromShell(outerWalls.concat(supportBeams), yPosition, silent);
        if (mixed.length < 4) {
            // Walls standing under or beside the beams of a beam ring break the loop walk.
            mixed = pointsFromOutline(outerWalls.concat(supportBeams), yPosition);
        }
        if (mixed.length > 3) {
            points = mixed;
        }
    }

    resetWallSides();
    return points;
}

export function isOuterShellClosed() {
    return getOuterWallPoints(false, true, true).length >= 4;
}

function resetWallSides() {
    walls = {
        left: [],
        right: [],
        front: [],
        back: []
    };
}

function pointsFromShell(members, yPosition, silent) {
    const startWall = getStartWall([...members]);
    if (!startWall) {
        return [];
    }

    const wallLoop = generateOuterWallLoop([...members], startWall, silent);
    resetWallSides();
    if (wallLoop.length <= 3) {
        return [];
    }

    getWallSides(wallLoop);
    return getLoopPoints(calculateOuterWallPoints(wallLoop), yPosition);
}

function pointsFromOutline(members, yPosition) {
    const rects = members.map((member) => {
        const bounds = new Box3().setFromObject(member);
        return {minX: bounds.min.x, maxX: bounds.max.x, minZ: bounds.min.z, maxZ: bounds.max.z};
    });
    return shellOutline(rects).map((p) => toFixedVector(new Vector3(p.x, yPosition, p.z)));
}

function getStartWall(outerWalls) {
    //Get wall with most lowest x coordinate
    let result = sortWallsByOrientation(outerWalls);
    let zOrientedWalls = result.z;
    let leftestWall = null;
    let minXPosition = null;
    for (let i = 0; i < zOrientedWalls.length; i++) {
        let currentZOrientedWall = zOrientedWalls[i];
        let currentWallSize = new Box3().setFromObject(currentZOrientedWall);

        if (i === 0) {
            leftestWall = currentZOrientedWall;
            minXPosition = currentWallSize.min.x;
            continue;
        }

        if (toFixedFloat(currentWallSize.min.x) <= toFixedFloat(minXPosition)) {
            leftestWall = currentZOrientedWall;
            minXPosition = currentWallSize.min.x;
        }
    }

    return leftestWall;
}

function sortWallsByOrientation(outerWalls) {
    let xOrientedWalls = [];
    let zOrientedWalls = [];

    for (let i = outerWalls.length - 1; i >= 0; i--) {
        let wallOrientation = getWallOrientation(outerWalls[i]);
        if (wallOrientation === "x") {
            xOrientedWalls.push(outerWalls[i]);
        } else {
            zOrientedWalls.push(outerWalls[i]);
        }
    }

    xOrientedWalls = sortBySubKey(xOrientedWalls, "position", "z");
    zOrientedWalls = sortBySubKey(zOrientedWalls, "position", "x");

    return {x: xOrientedWalls, z: zOrientedWalls};
}

export function generateOuterWallLoop(outerWalls, startWall, silent = false) {
    const report = (message) => {
        if (!silent) {
            console.error(message);
        }
    };

    if (!startWall) {
        return [];
    }

    let wallLoop = [];
    wallLoop.push(startWall);
    outerWalls.splice(outerWalls.indexOf(startWall), 1);

    let loopClosed = false;
   /*  let ignoredWalls = []; */
    let currentWall = null;

    while (!loopClosed) {
        currentWall = wallLoop[wallLoop.length - 1];
        let intersectingWalls = getIntersectingWalls(currentWall, [...outerWalls], true); //Geschnittene Wände bestimmen
        
        //let extendedIntersectingWalls = getIntersectingWalls(currentWall, [...outerWalls], true, true,true,false,false);
        //intersectingWalls = intersectingWalls.concat(extendedIntersectingWalls);

        //intersectingWalls = intersectingWalls.filter((x, xi) => !intersectingWalls.slice(xi + 1).some(y => y['uuid'] === x['uuid'])); //remove duplicate uuids?
        if (intersectingWalls.length === 0) {
            if (outerWalls.length === 0) { //Wenn keine Wände mehr zum schneiden sind. prüfe auf Schnitt mit ersten Wand um Loop zu schließen
                let intersectWithFirstWall = getIntersectingWalls(currentWall, [wallLoop[0]], true);
                if (intersectWithFirstWall.length > 0) {
                    //Loop closed
                    loopClosed = true;
                    break;
                }
            } else {
                report("Loop konnte nicht geschlossen werden. Kein Schnitt mit Wandindex 0 und keine neuen Schnittpunkte / Loop could not be closed. No intersection with wall index 0 and no outer walls left");
            }

            report("Keine Intersectingwalls gefunden / No intersecting walls found");
            break;
        }

        let nextWall = null; // after startWall (leftest) next wall is the lowestWall

        //Erste Wand (Weitesten Links), muss mit der am weitesten in -Z liegenden Wand schneiden
        //First Wall (Furthest Left), must intersect with the furthest wall in -Z (lowestWall, where nextWall=lowestWall)
        if (wallLoop.length === 1) {
            let lowestZ = null;
            let lowestWall = null;
            for (let i = 0; i < intersectingWalls.length; i++) {
                let center = new Box3().setFromObject(intersectingWalls[i]).getCenter(new Vector3());
                if (lowestZ === null || lowestZ.z > center.z) {
                    lowestZ = center;
                    lowestWall = intersectingWalls[i];
                }
            }

            nextWall = lowestWall;
        } else {
            //alle weiteren Wände ist der erste Intersect (Vorausgesetzt eine Wand hat immer nur 2 Schnittpunkte mit weiteren Auspunkten
            //Schnitt mit vorherigen Wänden wird verhindern durch Entfernen aus dem Prüfarray
            if (intersectingWalls.length === 1) {
                nextWall = intersectingWalls[0];
            } else {
                let currentWallPosition = currentWall.position;
                let intersectingWallPositions = [];
                for (let i = 0; i < intersectingWalls.length; i++) {
                    intersectingWallPositions.push(new Vector3().copy(intersectingWalls[i].position));
                }
                let totalPositionBounds = new Box3().setFromPoints([...intersectingWallPositions].concat(currentWallPosition));
                let totalCenter = totalPositionBounds.getCenter(new Vector3());
                let wallDirection = new Vector3().subVectors(totalCenter, currentWallPosition);

                let wallVectors = [];
                for (let i = 0; i < intersectingWallPositions.length; i++) {
                    wallVectors.push(new Vector3().subVectors(intersectingWallPositions[i], currentWallPosition));
                }

                let smallestAngle = null;
                let index = null;
                for (let i = 0; i < wallVectors.length; i++) {
                    let angle = signedAngle(wallDirection, wallVectors[i]);
                    if (angle === null || angle > smallestAngle) {
                        smallestAngle = angle;
                        index = i;
                    }
                }

                //Remove unused walls;
                for (let i = 0; i < intersectingWalls.length; i++) {
                    if (i !== index) {
                        let ignoreWall = intersectingWalls[i];
                        outerWalls.splice(outerWalls.indexOf(ignoreWall), 1);
                    }
                }
                nextWall = intersectingWalls[index];
            }
        }

        if (nextWall === null) {
            report("Nächste Wand konnte nicht bestimmt werden.");
        }

        wallLoop.push(nextWall);
        outerWalls.splice(outerWalls.indexOf(nextWall), 1);
    }

    if (getIntersectingWalls(currentWall, [wallLoop[0]], true).length === 0) {
        wallLoop = [];
        loopClosed = false;
    }

    if (!loopClosed) {
        return [];
    }

    return wallLoop;
}

function getWallSides(wallLoop) {
    let currentSide = wallSides.left;

    let lastPoints = null;
    let lastEnd = null;
    for (let i = 0; i < wallLoop.length; i++) {
        let wall = wallLoop[i];

        if (i === 0) {
            //Init für erste Wand
            walls.left.push(wall);
            lastPoints = getWallBoundPoints(wall, wallSides.left);
            lastEnd = lastPoints.end;
        } else {
            //orientierung der Wand um Basispunkte zu bestimmen
            let orientation = getWallOrientation(wall);
            if (wall.name === supportBeamMeshName) {
                let length = wall.userData.width;
                let supportBeamBounds = new Box3().setFromObject(wall);
                let size = supportBeamBounds.getSize(new Vector3());
                orientation = toFixedFloat(length) === toFixedFloat(size.x) ? "x" : "z";
            }
            let points;
            if (orientation === "x") {
                points = getWallBoundPoints(wall, wallSides.back);
            } else {
                points = getWallBoundPoints(wall, wallSides.left);
            }

            //Start und Ende swappen bei Bedarf
            let distanceStart = lastEnd.distanceTo(points.start);
            let distanceEnd = lastEnd.distanceTo(points.end);

            if (distanceEnd < distanceStart) {
                let tmp = new Vector3().copy(points.end);
                points.end = new Vector3().copy(points.start);
                points.start = tmp;
            }

            //Winkel zwischen Wänden berechnen
            let currentVector = new Vector3().subVectors(points.end, points.start);
            let lastVector = new Vector3().subVectors(lastPoints.end, lastPoints.start);

            let angle = signedAngle(lastVector, currentVector);
            let leftTurn = angle > 0;

            /* if (Math.abs(angle) === 0) {

            } else  */if (!leftTurn) {
                currentSide++;
                if (currentSide > 4) {
                    currentSide -= 4;
                }
            } else {
                currentSide--;
                if (currentSide < 1) {
                    currentSide += 4;
                }
            }

            lastPoints = points;
            lastEnd = points.end;

            //Speichern der Wand in jeweilige Position
            if (currentSide === wallSides.left) {
                walls.left.push(wall);
            } else if (currentSide === wallSides.back) {
                walls.back.push(wall);
            } else if (currentSide === wallSides.right) {
                walls.right.push(wall);
            } else if (currentSide === wallSides.front) {
                walls.front.push(wall);
            }
        }
    }
}

function calculateOuterWallPoints(wallLoop) {
    let wallsWithPoints = [];

    for (let i = 0; i < wallLoop.length; i++) {
        let wall = wallLoop[i];
        let side = getOuterWallSide(wall);
        let wallPoints = getWallBoundPoints(wall, side);

        if (i > 0) {
            //Korrektur der Start und Endpunkte in Abhängigkeit zur vorherigen Wand (Können verdreht sein
            let lastWall = wallLoop[i - 1];
            let lastSide = getOuterWallSide(lastWall);
            let lastWallPoints = getWallBoundPoints(lastWall, lastSide);
            let lastEnd = lastWallPoints.end;

            let distanceStart = lastEnd.distanceTo(wallPoints.start);
            let distanceEnd = lastEnd.distanceTo(wallPoints.end);

            if (distanceEnd < distanceStart) {
                let tmp = new Vector3().copy(wallPoints.end);
                wallPoints.end = new Vector3().copy(wallPoints.start);
                wallPoints.start = tmp;
            }
        }

        wallsWithPoints.push({wall: wall, side: side, start: wallPoints.start, end: wallPoints.end});
    }

    return wallsWithPoints;
}

function getOuterWallSide(wall) {
    if (walls.left.indexOf(wall) !== -1) {
        return wallSides.left;
    } else if (walls.right.indexOf(wall) !== -1) {
        return wallSides.right;
    } else if (walls.front.indexOf(wall) !== -1) {
        return wallSides.front;
    } else if (walls.back.indexOf(wall) !== -1) {
        return wallSides.back;
    }

    return -1;
}

function getWallBoundPoints(wall, side,doDebug = false) {
    
    let bounds = new Box3().setFromObject(wall);
    
    if (doDebug) {
        console.log("getting bounds of",wall,bounds);
    }

    if (wall.name === supportBeamMeshName) {
        const length = wall.userData.width;
        const size = bounds.getSize(new Vector3());
        const alongX = toFixedFloat(length) === toFixedFloat(size.x);
        const sideIsLong = alongX
            ? (side === wallSides.front || side === wallSides.back)
            : (side === wallSides.left || side === wallSides.right);
        // The turn test asks for a side before the beam has one. A centerline keeps that angle at 90 degrees.
        // Once the side is known, fall through and use the outer long edge, the same way a wall does.
        if (!sideIsLong) {
            if (alongX) {
                const z = toFixedFloat((bounds.min.z + bounds.max.z) / 2);
                return {
                    start: new Vector3(toFixedFloat(bounds.min.x), 0, z),
                    end: new Vector3(toFixedFloat(bounds.max.x), 0, z)
                };
            }
            const x = toFixedFloat((bounds.min.x + bounds.max.x) / 2);
            return {
                start: new Vector3(x, 0, toFixedFloat(bounds.min.z)),
                end: new Vector3(x, 0, toFixedFloat(bounds.max.z))
            };
        }
    }

    if (side === wallSides.left) {
        return {
            start: new Vector3(toFixedFloat(bounds.min.x), 0, toFixedFloat(bounds.max.z)),
            end: new Vector3(toFixedFloat(bounds.min.x), 0, toFixedFloat(bounds.min.z))
        };
    } else if (side === wallSides.right) {
        return {
            start: new Vector3(toFixedFloat(bounds.max.x), 0, toFixedFloat(bounds.min.z)),
            end: new Vector3(toFixedFloat(bounds.max.x), 0, toFixedFloat(bounds.max.z))
        };
    } else if (side === wallSides.front) {
        return {
            start: new Vector3(toFixedFloat(bounds.max.x), 0, toFixedFloat(bounds.max.z)),
            end: new Vector3(toFixedFloat(bounds.min.x), 0, toFixedFloat(bounds.max.z))
        };
    } else if (side === wallSides.back) {
        return {
            start: new Vector3(toFixedFloat(bounds.min.x), 0, toFixedFloat(bounds.min.z)),
            end: new Vector3(toFixedFloat(bounds.max.x), 0, toFixedFloat(bounds.min.z))
        };
    }

    return null;
}

function getLoopPoints(wallLoop, yPosition = 0) {
    let loop = [];

    for (let i = 0; i < wallLoop.length; i++) {
        let currentWall = wallLoop[i];
        let nextWall = wallLoop[i === wallLoop.length - 1 ? 0 : i + 1];

        let xValues = [currentWall.end.x, nextWall.start.x];
        let zValues = [currentWall.end.z, nextWall.start.z];

        let currentVector = new Vector3().subVectors(currentWall.end, currentWall.start);
        let nextVector = new Vector3().subVectors(nextWall.end, nextWall.start);
        let angle = signedAngle(currentVector, nextVector);
        let vectorDirection = getVectorDirection(currentVector);

        let xVal, zVal;
        if (vectorDirection === "negativeZ") {
            xVal = Math.min(...xValues);
            zVal = angle < 0 ? Math.min(...zValues) : Math.max(...zValues);
        } else if (vectorDirection === "positiveX") {
            xVal = angle < 0 ? Math.max(...xValues) : Math.min(...xValues);
            zVal = Math.min(...zValues);
        } else if (vectorDirection === "positiveZ") {
            xVal = Math.max(...xValues);
            zVal = angle < 0 ? Math.max(...zValues) : Math.min(...zValues);
        } else if (vectorDirection === "negativeX") {
            xVal = angle < 0 ? Math.min(...xValues) : Math.max(...xValues);
            zVal = Math.max(...zValues);
        }

        loop.push(toFixedVector(new Vector3(xVal, yPosition, zVal)));
    }


    return filterLoopPoints(loop);
}

function filterLoopPoints(points) {
    let filteredPoints = [];
    for (let i = 0; i < points.length; i++) {
        const a = points[(i - 1 + points.length) % points.length]; // Previous point
        const b = points[i]; // Current point
        const c = points[(i + 1) % points.length]; // Next point

        if (!isOnLine(a, b, c)) {
            filteredPoints.push(b);
        }
    }
    return filteredPoints;
}

function isOnLine(a, b, c) {
    const ab = new Vector3().subVectors(b, a);
    const ac = new Vector3().subVectors(c, a);
    return ab.cross(ac).length() === 0; // Check if cross product is zero (on the same line)
}

function getVectorDirection(vector) {
    if (Math.abs(vector.x) > Math.abs(vector.z)) {
        if (vector.x > 0) {
            return "positiveX";
        } else {
            return "negativeX";
        }
    } else {
        if (vector.z > 0) {
            return "positiveZ";
        } else {
            return "negativeZ";
        }
    }
}

function getSupportBeamsOutsideBuilding(roofPoints, allSupportBeams) {
    //TODO: Lässt sich noch optimieren
    //Rähme filtern die nicht innerhalb der roofPoints liegen
    //TODO: Can be further optimized
    //Frames filter those not lying within the roofPoints
    //console.log("filtering outside support beams");
    let newRoofPoints = copyVectorArray(roofPoints);
    let roofPointsClockwise = pathPointsClockwise(newRoofPoints);
    newRoofPoints = Roof.extendRoofPointsByLength(newRoofPoints, roofPointsClockwise, 0.005); //Vergrößern um minimale Toleranz damit die Außenwände nicht entfernt werden
    let shape = createShapeFromPoints(newRoofPoints);
    for (let i = allSupportBeams.length - 1; i >= 0; i--) {
        let mesh = allSupportBeams[i];
        let points = getPointsFromBoundingBox2D(new Box3().setFromObject(mesh, true));
        let outside = false;
        for (let j = 0; j < points.length; j++) {
            if (!shapeContainsPoints(shape, points[j])) {
                outside = true;
                break;
            }
        }

        if (!outside) {
            allSupportBeams.splice(i, 1);
        }
    }

/*     console.log("filtered outside support beams",allSupportBeams);
    allSupportBeams.forEach((beam) => {
        let debugObject = new Mesh(new BoxGeometry(0.1, 0.1, 0.1), new MeshBasicMaterial({color: 0xff0000}));
        debugObject.position.set(beam.position.x, beam.position.y, beam.position.z);
        debugObject.name = "debugObject";
        buildingGroup.add(debugObject);
    }); */
    return allSupportBeams;
}
